/* eslint-disable no-case-declarations */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import getWalletApi, {
  ResponseGetWallet,
  ResponseGetWalletType,
} from '@/apis/get-wallet.api';
import QUERY_KEYS from '@/constants/query-keys';
import useUser from '@/hooks//use-user.hook';
import useDestination from '@/hooks/use-destination.hook';
import useGetPlan from '@/hooks/use-get-plan.hook';
import useGetReport from '@/hooks/use-get-report.hook';
import { ActionType, Transaction, TransactionType } from '@/types/wallet';

export default function useGetWallet() {
  const { getToken } = useUser();
  const { reports } = useGetReport();
  const { plans } = useGetPlan();
  const { t } = useTranslation();
  const { getLocateName } = useDestination();

  const { isPending: isPendingWallet, data: walletData } = useQuery({
    queryKey: QUERY_KEYS.WALLET,
    queryFn: async () => {
      const token = await getToken();
      return getWalletApi(token);
    },
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: true,
  });

  const transactions = useMemo(() => {
    const mapActionTypeTranslation: Record<ActionType, string> = {
      [ActionType.WELCOME_BONUS]: 'wallet.action_types.welcome_bonus',
      [ActionType.REPORT_AQUISITION]: 'wallet.action_types.report_acquisition',
      [ActionType.PLAN_PURCHASE]: 'wallet.action_types.plan_purchase',
      [ActionType.NOT_DEFINED]: 'wallet.action_types.not_defined',
      [ActionType.REPORT_UPGRADE]: 'wallet.action_types.report_upgrade',
      [ActionType.REPORT_SYNC]: 'wallet.action_types.report_sync',
    };

    const getTransactionTypeFromGetWallet = {
      [ResponseGetWalletType.ADD]: TransactionType.ADD,
      [ResponseGetWalletType.DEDUCT]: TransactionType.DEDUCT,
    };

    const mapTransaction = (
      data: ResponseGetWallet['transactions'][number]
    ): Transaction => {
      let action: string = data.action;

      if (data.action in ActionType) {
        const key: string = mapActionTypeTranslation[data.action as ActionType];
        const opts: Record<string, string> = {};

        switch (data.action) {
          case ActionType.PLAN_PURCHASE:
            const refPlan = data.reference as {
              userPlanType: string;
              orderId: string;
            };
            const referencePlan = plans.find(
              p => p.type === refPlan?.userPlanType
            );
            opts['plan'] = referencePlan?.name || refPlan.userPlanType || 'N/A';
            opts['order'] = refPlan?.orderId || 'N/A';
            break;
          case ActionType.REPORT_AQUISITION:
          case ActionType.REPORT_UPGRADE:
          case ActionType.REPORT_SYNC:
            const ref = data.reference as {
              reportId: string;
              reportPlanType: string;
              userPlanType: string;
            };
            const report = reports.find(r => r.id === ref?.reportId);
            const target = report
              ? getLocateName(report.targetDestination)
              : 'N/A';
            opts['target'] = target;
            opts['userPlan'] =
              plans.find(({ type }) => type === ref?.userPlanType)?.name ||
              ref.userPlanType ||
              'N/A';
            opts['plan'] =
              plans.find(({ type }) => type === ref?.reportPlanType)?.name ||
              ref.reportPlanType ||
              'N/A';
            break;
        }
        action = t(key, opts);
      }

      return {
        id: data.id,
        date: new Date(data.date),
        amount: data.amount,
        balanceAfter: data.balanceAfter,
        action,
        type: getTransactionTypeFromGetWallet[data.type],
        link: data.link,
      };
    };

    return walletData?.transactions.map(mapTransaction) ?? [];
  }, [getLocateName, plans, reports, t, walletData?.transactions]);

  return {
    isPendingWallet,
    wallet: {
      balance: walletData?.balance ?? 0,
      transactions,
    },
  };
}
